import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  CalculatorIcon,
  AdjustmentsHorizontalIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import type { 
  MatrizAvaliacaoContent, 
  PerfilConfig, 
  PesosDimensaoConfig 
} from '../../services/contentSnapshotService';
import type { ValidationError } from '../../services/contentAdminService';

interface MatrixEditorProps {
  versionId: string;
  content: any;
  onChange: (content: any) => void;
  validationErrors: ValidationError[];
}

interface ProfileWeightForm {
  perfil_id: string;
  nome: string;
  pesos: Record<string, number>;
}

export const MatrixEditor: React.FC<MatrixEditorProps> = ({
  versionId,
  content,
  onChange,
  validationErrors,
}) => {
  const [editingProfile, setEditingProfile] = useState<string | null>(null);
  const [profileForm, setProfileForm] = useState<ProfileWeightForm>({
    perfil_id: '',
    nome: '',
    pesos: {},
  });

  const matrix: MatrizAvaliacaoContent = content.evaluation_matrix || {
    versao: '1.0',
    data_criacao: new Date().toISOString(),
    descricao: 'Matriz de avaliação para cálculo de perfis',
    algoritmo: {
      tipo: 'ponderacao_dimensional',
      descricao: 'Cálculo baseado em pesos por dimensão para cada perfil',
    },
    dimensoes: [],
    perfis: {},
    configuracao_calculo: {
      escala_minima: 1,
      escala_maxima: 5,
      formula_normalizacao: 'percentual',
      threshold_perfil_secundario: 0.15,
    },
  };

  const updateMatrix = (newMatrix: MatrizAvaliacaoContent) => {
    onChange({
      ...content,
      evaluation_matrix: newMatrix,
    });
  };

  // Get available dimensions from questions content
  const availableDimensions = content.questions?.dimensoes || [];
  const matrixDimensions = matrix.dimensoes || [];

  // Profile management
  const handleAddProfile = () => {
    const newProfileId = prompt('ID do novo perfil (ex: equilibrada):');
    if (!newProfileId || Object.keys(matrix.perfis).includes(newProfileId)) return;

    const newProfile: PerfilConfig = {
      nome: newProfileId.charAt(0).toUpperCase() + newProfileId.slice(1),
      descricao: 'Descrição do perfil',
      pesos_dimensoes: {},
    };

    // Initialize weights for all dimensions
    matrixDimensions.forEach(dim => {
      newProfile.pesos_dimensoes[dim.id] = 1.0;
    });

    updateMatrix({
      ...matrix,
      perfis: {
        ...matrix.perfis,
        [newProfileId]: newProfile,
      },
    });
  };

  const handleEditProfile = (profileId: string) => {
    const profile = matrix.perfis[profileId];
    if (!profile) return;

    setProfileForm({
      perfil_id: profileId,
      nome: profile.nome,
      pesos: { ...profile.pesos_dimensoes },
    });
    setEditingProfile(profileId);
  };

  const handleSaveProfile = () => {
    if (!profileForm.perfil_id || !profileForm.nome.trim()) return;

    const updatedProfile: PerfilConfig = {
      nome: profileForm.nome,
      descricao: matrix.perfis[profileForm.perfil_id]?.descricao || 'Descrição do perfil',
      pesos_dimensoes: { ...profileForm.pesos },
    };

    updateMatrix({
      ...matrix,
      perfis: {
        ...matrix.perfis,
        [profileForm.perfil_id]: updatedProfile,
      },
    });

    setEditingProfile(null);
    setProfileForm({
      perfil_id: '',
      nome: '',
      pesos: {},
    });
  };

  const handleDeleteProfile = (profileId: string) => {
    if (!confirm(`Tem certeza que deseja excluir o perfil "${profileId}"?`)) return;

    const newPerfis = { ...matrix.perfis };
    delete newPerfis[profileId];

    updateMatrix({
      ...matrix,
      perfis: newPerfis,
    });
  };

  const handleUpdateWeight = (dimensionId: string, weight: number) => {
    const clampedWeight = Math.max(0, Math.min(5, weight));
    setProfileForm({
      ...profileForm,
      pesos: {
        ...profileForm.pesos,
        [dimensionId]: clampedWeight,
      },
    });
  };

  // Dimension synchronization
  const handleSyncDimensions = () => {
    const newDimensions = availableDimensions.map(dim => ({
      id: dim.id,
      nome: dim.nome,
      descricao: dim.descricao,
      peso_base: 1.0,
    }));

    // Update all profiles to include new dimensions
    const updatedPerfis = { ...matrix.perfis };
    Object.keys(updatedPerfis).forEach(profileId => {
      const profile = updatedPerfis[profileId];
      newDimensions.forEach(dim => {
        if (!(dim.id in profile.pesos_dimensoes)) {
          profile.pesos_dimensoes[dim.id] = 1.0;
        }
      });
      
      // Remove weights for dimensions that no longer exist
      Object.keys(profile.pesos_dimensoes).forEach(dimId => {
        if (!newDimensions.find(d => d.id === dimId)) {
          delete profile.pesos_dimensoes[dimId];
        }
      });
    });

    updateMatrix({
      ...matrix,
      dimensoes: newDimensions,
      perfis: updatedPerfis,
    });
  };

  const handleUpdateBasicInfo = (field: string, value: string | number) => {
    if (field.includes('configuracao_calculo.')) {
      const configField = field.split('.')[1];
      updateMatrix({
        ...matrix,
        configuracao_calculo: {
          ...(matrix.configuracao_calculo || {}),
          [configField]: value,
        },
      });
    } else {
      updateMatrix({
        ...matrix,
        [field]: value,
      });
    }
  };

  const getValidationErrors = (field: string) => {
    return validationErrors.filter(error => error.field.includes(field));
  };

  const calculateProfileTotal = (pesos: Record<string, number>) => {
    const total = Object.values(pesos).reduce((sum, peso) => sum + peso, 0);
    return total.toFixed(2);
  };

  const isDimensionsSynced = () => {
    return matrixDimensions.length === availableDimensions.length &&
           matrixDimensions.every(matrixDim => 
             availableDimensions.find(availableDim => availableDim.id === matrixDim.id)
           );
  };

  return (
    <div className="space-y-8">
      {/* Basic Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Configuração da Matriz
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Versão
            </label>
            <input
              type="text"
              value={matrix.versao}
              onChange={(e) => handleUpdateBasicInfo('versao', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Algoritmo
            </label>
            <input
              type="text"
              value={matrix.algoritmo?.tipo || ''}
              onChange={(e) => handleUpdateBasicInfo('algoritmo', { ...(matrix.algoritmo || {}), tipo: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição
            </label>
            <textarea
              value={matrix.descricao}
              onChange={(e) => handleUpdateBasicInfo('descricao', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Calculation Configuration */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            Configuração de Cálculo
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Escala Mínima
              </label>
              <input
                type="number"
                value={matrix.configuracao_calculo?.escala_minima || 1}
                onChange={(e) => handleUpdateBasicInfo('configuracao_calculo.escala_minima', parseInt(e.target.value))}
                min="1"
                max="5"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Escala Máxima
              </label>
              <input
                type="number"
                value={matrix.configuracao_calculo?.escala_maxima || 5}
                onChange={(e) => handleUpdateBasicInfo('configuracao_calculo.escala_maxima', parseInt(e.target.value))}
                min="1"
                max="10"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fórmula
              </label>
              <select
                value={matrix.configuracao_calculo?.formula_normalizacao || 'percentual'}
                onChange={(e) => handleUpdateBasicInfo('configuracao_calculo.formula_normalizacao', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="percentual">Percentual</option>
                <option value="media_ponderada">Média Ponderada</option>
                <option value="z_score">Z-Score</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Threshold
              </label>
              <input
                type="number"
                value={matrix.configuracao_calculo?.threshold_perfil_secundario || 0.15}
                onChange={(e) => handleUpdateBasicInfo('configuracao_calculo.threshold_perfil_secundario', parseFloat(e.target.value))}
                step="0.01"
                min="0"
                max="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dimensions Sync */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Dimensões da Matriz
          </h3>
          <div className="flex items-center gap-3">
            {!isDimensionsSynced() && (
              <div className="flex items-center gap-2 text-amber-600">
                <ExclamationTriangleIcon className="w-4 h-4" />
                <span className="text-sm">Dimensões desatualizadas</span>
              </div>
            )}
            <button
              onClick={handleSyncDimensions}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <AdjustmentsHorizontalIcon className="w-4 h-4" />
              Sincronizar com Questões
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {matrixDimensions.map((dimensao) => (
            <div key={dimensao.id} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">{dimensao.nome}</h4>
              <p className="text-sm text-gray-600 mb-3">{dimensao.descricao}</p>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Peso Base
                </label>
                <input
                  type="number"
                  value={dimensao.peso_base}
                  onChange={(e) => {
                    const newDimensoes = [...matrixDimensions];
                    const index = newDimensoes.findIndex(d => d.id === dimensao.id);
                    if (index >= 0) {
                      newDimensoes[index] = { ...newDimensoes[index], peso_base: parseFloat(e.target.value) || 0 };
                      updateMatrix({ ...matrix, dimensoes: newDimensoes });
                    }
                  }}
                  step="0.1"
                  min="0"
                  max="5"
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          ))}
        </div>

        {matrixDimensions.length === 0 && (
          <div className="text-center py-8">
            <AdjustmentsHorizontalIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma dimensão configurada
            </h3>
            <p className="text-gray-600 mb-4">
              Sincronize as dimensões das questões para começar a configurar a matriz.
            </p>
            <button
              onClick={handleSyncDimensions}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <AdjustmentsHorizontalIcon className="w-4 h-4" />
              Sincronizar Dimensões
            </button>
          </div>
        )}
      </div>

      {/* Profile Weights */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Perfis e Pesos ({Object.keys(matrix.perfis).length})
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Configure os pesos de cada dimensão para cada perfil
            </p>
          </div>
          
          <button
            onClick={handleAddProfile}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Novo Perfil
          </button>
        </div>

        {/* Profile Editor Form */}
        <AnimatePresence>
          {editingProfile && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 border border-blue-200 rounded-lg p-4 bg-blue-50"
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Perfil
                  </label>
                  <input
                    type="text"
                    value={profileForm.nome}
                    onChange={(e) => setProfileForm({ ...profileForm, nome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Pesos por Dimensão
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {matrixDimensions.map((dimensao) => (
                      <div key={dimensao.id} className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600">
                          {dimensao.nome}
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="range"
                            min="0"
                            max="5"
                            step="0.1"
                            value={profileForm.pesos[dimensao.id] || 1}
                            onChange={(e) => handleUpdateWeight(dimensao.id, parseFloat(e.target.value))}
                            className="flex-1"
                          />
                          <span className="w-12 text-sm text-gray-700 font-mono">
                            {(profileForm.pesos[dimensao.id] || 1).toFixed(1)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-3 p-2 bg-gray-100 rounded text-sm">
                    <span className="font-medium">Total: {calculateProfileTotal(profileForm.pesos)}</span>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setEditingProfile(null);
                      setProfileForm({ perfil_id: '', nome: '', pesos: {} });
                    }}
                    className="px-3 py-1.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 inline mr-1" />
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={!profileForm.nome.trim()}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <CheckIcon className="w-4 h-4 inline mr-1" />
                    Salvar
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Profiles List */}
        <div className="space-y-4">
          {Object.entries(matrix.perfis).map(([profileId, profile]) => (
            <div key={profileId} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-gray-900">{profile.nome}</h4>
                  <p className="text-sm text-gray-600">{profile.descricao}</p>
                </div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEditProfile(profileId)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteProfile(profileId)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {matrixDimensions.map((dimensao) => (
                  <div key={dimensao.id} className="text-center">
                    <div className="text-xs text-gray-600 mb-1">{dimensao.nome}</div>
                    <div className="font-mono text-sm font-medium">
                      {(profile.pesos_dimensoes[dimensao.id] || 0).toFixed(1)}
                    </div>
                  </div>
                ))}
                
                <div className="text-center border-l border-gray-200 pl-3">
                  <div className="text-xs text-gray-600 mb-1">Total</div>
                  <div className="font-mono text-sm font-medium">
                    {calculateProfileTotal(profile.pesos_dimensoes)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {Object.keys(matrix.perfis).length === 0 && (
          <div className="text-center py-12">
            <CalculatorIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum perfil configurado
            </h3>
            <p className="text-gray-600 mb-4">
              Adicione perfis para configurar os pesos da matriz de avaliação.
            </p>
            <button
              onClick={handleAddProfile}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Adicionar Primeiro Perfil
            </button>
          </div>
        )}
      </div>

      {/* Validation Errors */}
      {getValidationErrors('matrix').length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-red-800 mb-2">
            Erros de Validação:
          </h4>
          <ul className="text-sm text-red-700 space-y-1">
            {getValidationErrors('matrix').map((error, index) => (
              <li key={index}>• {error.message}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};